import { useNavigate } from 'react-router-dom'

import {
  Box,
  Checkbox,
  Colors,
  Icon,
  List,
  MediumIcon,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'

import { useMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import ItemAssignees from 'modules/processes/components/ItemAssignees'
import { AssigneeStrategy } from 'modules/processes/components/ItemAssignees/types'
import type { MaterializedItem } from 'modules/processes/domain'

import type { ActionItem } from './useActionItems'

interface Props {
  isCurrentStep: boolean
  materializedItem: MaterializedItem
  navigateTo?: (() => void) | undefined
  actionItems: ActionItem[]
}

enum CustomStatus {
  ActionCompletedByUser = 'action_completed_by_user',
}

export const MaterializedAction = ({
  materializedItem,
  isCurrentStep,
  navigateTo,
  actionItems,
}: Props) => {
  const navigate = useNavigate()
  const [open, toggle] = useToggle()
  const title = materializedItem.item?.action?.title || materializedItem?.item?.title
  const currentActionItem = actionItems.find((action) => action.action_id === materializedItem.id)

  const navigateToResource = () => {
    if (navigateTo) return navigateTo

    const route = currentActionItem?.navigate

    if (!route) return undefined

    return () => navigate(route)
  }

  const customActionCompleted =
    currentActionItem?.completed != undefined && currentActionItem.completed

  const actionCompleted =
    currentActionItem?.completed != undefined
      ? currentActionItem.completed
      : materializedItem.status === 'completed'

  const actionFailed = materializedItem.status === 'failed'

  const actionInProgress = !actionCompleted && !actionFailed

  const accessIds = materializedItem.assigeeIds || []

  const reason = actionInProgress
    ? i18n.t('processes.materialize_process.actions.pending')
    : materializedItem.reason

  const icon =
    currentActionItem && !currentActionItem?.completed ? (
      <Checkbox size="large" disabled checked={false} onChange={navigateToResource} />
    ) : (
      <ActionIcon
        status={
          customActionCompleted ? CustomStatus.ActionCompletedByUser : materializedItem.status
        }
      />
    )

  return (
    <List.ClickableItem navigateTo={navigateToResource()} key={materializedItem.id}>
      <Box flexDirection="row" gap="s16">
        <List.ActionItem>{icon}</List.ActionItem>

        <Text.Normal
          weight="medium"
          color={isCurrentStep ? undefined : 'grey600'}
          stroked={actionCompleted}
          ellipsis
        >
          {title}
        </Text.Normal>
        {reason && (
          <Tooltip.Alt attachment="top center" label={reason} open={open} toggle={toggle}>
            <Icon.Medium icon="info-circle-line" color="iconSecondary" />
          </Tooltip.Alt>
        )}
      </Box>

      <Box gap="s16" flexDirection="row" alignItems="center" flexNoShrink>
        <Box position="relative">
          <ItemAssignees
            count={accessIds.length}
            accessIds={accessIds}
            assigneeStrategy={AssigneeStrategy.MANUAL_SELECTION}
          />
        </Box>
      </Box>
    </List.ClickableItem>
  )
}

const ActionIcon = ({ status }: { status: string }) => {
  const styles = useMemo((): {
    icon: MediumIcon
    backgroundColor: Colors
    color: Colors
  } => {
    switch (status) {
      case CustomStatus.ActionCompletedByUser:
        return {
          icon: 'check',
          backgroundColor: 'malibu200',
          color: 'malibu1000',
        }
      case 'completed':
        return {
          icon: 'thunder',
          backgroundColor: 'tangerine200',
          color: 'tangerine1000',
        }
      case 'failed':
        return {
          icon: 'alert',
          backgroundColor: 'champagne200',
          color: 'champagne1000',
        }
      default:
        return {
          icon: 'spinner',
          backgroundColor: 'malibu200',
          color: 'malibu1000',
        }
    }
  }, [status])

  return (
    <Box background={styles.backgroundColor} borderRadius={{ all: 'abs008' }}>
      <Icon.Medium icon={styles.icon} color={styles.color}></Icon.Medium>
    </Box>
  )
}
